import { fetchOpenAIText } from "../utils/openaiClient";

export function useJumbledSentenceGeneratorLogic({
  jumbledSentenceInput, setJumbledSentenceInput, jumbledSentenceMode, setJumbledSentenceMode,
  jumbledSentenceLevel, jumbledSentenceCount,
  setJumbledSentenceLoading, generatedJumbledSentence, setGeneratedJumbledSentence,
  currentTab, onInsertToBoard, activeEditor,
  setShowJumbledSentenceGenerator, setSelectedTemplate,
}) {
  const handleJumbledSentenceGenerate = async () => {
    if (!jumbledSentenceInput.trim()) return;

    if (jumbledSentenceMode === 'custom') {
      const lines = jumbledSentenceInput
        .split('\n')
        .map(l => l.trim())
        .filter(Boolean);
      if (!lines.length) return;
      setGeneratedJumbledSentence({
        title: 'Word Order Exercise',
        level: jumbledSentenceLevel,
        sentences: lines.map((sentence, i) => ({ id: i + 1, sentence })),
      });
      return;
    }

    setJumbledSentenceLoading(true);
    try {
      const levelSpecs = {
        A1: '4–7 words per sentence; only present simple/continuous, "to be"; concrete everyday vocabulary; simple SVO order.',
        A2: '6–10 words; past simple, "going to" future, basic modals; avoid subordinate clauses.',
        B1: '8–14 words; past continuous, present perfect, first conditional, common phrasal verbs, one simple subordinate clause allowed.',
        B2: '10–18 words; perfect tenses, second/third conditional, passive voice, reported speech.',
        C1: '12–22 words; complex subordinate clauses, inversion, academic/journalistic register.',
        C2: '14–26 words; near-native register, subtle idiomatic/discourse distinctions.',
      };
      const buildPrompt = (count) => `Create a word order (jumbled sentence) exercise for ${jumbledSentenceLevel} level students.
Topic or vocabulary: ${jumbledSentenceInput}

Generate exactly ${count} sentences appropriate for ${jumbledSentenceLevel} level.
Level requirements (match strictly): ${levelSpecs[jumbledSentenceLevel] || ''}
Each sentence should be a complete, natural English sentence that tests word order.
The sentences should be varied and engaging, and grammatically unambiguous once reordered
(i.e. there should be exactly one natural correct word order).

Return ONLY valid JSON in this exact format:
{
  "title": "Word Order Exercise",
  "level": "${jumbledSentenceLevel}",
  "sentences": [
    { "id": 1, "sentence": "She goes to school every day." },
    { "id": 2, "sentence": "They were playing football in the park." }
  ]
}`;

      const runOnce = async (count) => {
        const raw = await fetchOpenAIText({ prompt: buildPrompt(count), temperature: 0.7, maxTokens: 800 });
        if (!raw) return null;
        let contentStr = raw.trim();
        if (contentStr.startsWith('```json')) {
          contentStr = contentStr.replace(/^```json[\r\n]+/, '').replace(/```$/, '').trim();
        } else if (contentStr.startsWith('```')) {
          contentStr = contentStr.replace(/^```[\r\n]+/, '').replace(/```$/, '').trim();
        }
        try {
          return JSON.parse(contentStr);
        } catch (_) {
          return null;
        }
      };

      let parsed = await runOnce(jumbledSentenceCount);
      // Retry once for whatever's missing rather than padding with
      // duplicates or silently accepting an under-delivered set.
      if (parsed && Array.isArray(parsed.sentences) && parsed.sentences.length < jumbledSentenceCount) {
        const missing = jumbledSentenceCount - parsed.sentences.length;
        const fix = await runOnce(missing);
        if (fix && Array.isArray(fix.sentences)) {
          const maxId = parsed.sentences.reduce((m, s) => Math.max(m, s.id || 0), 0);
          parsed = { ...parsed, sentences: [...parsed.sentences, ...fix.sentences.map((s, i) => ({ ...s, id: maxId + i + 1 }))] };
        }
      }

      if (!parsed || !Array.isArray(parsed.sentences) || parsed.sentences.length === 0) {
        throw new Error('Could not generate the exercise — the AI response was invalid. Please try again.');
      }
      setGeneratedJumbledSentence(parsed);
    } catch (e) {
      alert(e?.message || 'Could not generate the exercise. Please try again.');
    } finally {
      setJumbledSentenceLoading(false);
    }
  };

  const handleJumbledSentenceInsert = () => {
    if (!generatedJumbledSentence) return;
    if (currentTab > 0 && onInsertToBoard) {
      onInsertToBoard(generatedJumbledSentence, 'jumbledsentence');
      setShowJumbledSentenceGenerator(false);
      setJumbledSentenceInput("");
      setGeneratedJumbledSentence(null);
      setSelectedTemplate(null);
      return;
    }
    if (activeEditor) {
      activeEditor.chain().focus().insertJumbledSentenceExercise({
        sentences: generatedJumbledSentence.sentences,
        title: generatedJumbledSentence.title,
        level: generatedJumbledSentence.level,
      }).run();
    }
    setShowJumbledSentenceGenerator(false);
    setJumbledSentenceInput("");
    setGeneratedJumbledSentence(null);
    setSelectedTemplate(null);
  };

  const handleJumbledSentenceCancel = () => {
    setShowJumbledSentenceGenerator(false);
    setJumbledSentenceInput("");
    setGeneratedJumbledSentence(null);
    setJumbledSentenceMode('generate');
    setSelectedTemplate(null);
  };

  return { handleJumbledSentenceGenerate, handleJumbledSentenceInsert, handleJumbledSentenceCancel };
}
